import { yupResolver } from '@hookform/resolvers/yup'
import { Typography } from '@/app/components/Typography'
import { useMemo, useRef } from 'react'
import * as yup from 'yup'

import {
  Controller,
  FormProvider,
  useForm,
  type Resolver,
} from 'react-hook-form'

import { ErrorMessage } from '@/app/components/ErrorMessage'
import { Input } from '@/app/components/Input'
import {
  INotificationStatus,
  useNotificationContext,
} from '@/app/components/Notification'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Headline2 } from '@/app/components/Typography'
import { useUserProfile } from '@/app/hooks/data-hooks/useUserProfile'
import { useUserProfileMutation } from '@/app/hooks/data-hooks/useUserProfileMutation'
import { getErrorMessage } from '@/app/lib/getErrorMessage'
import { CompanySize } from '@/types/profile'
import { ProgressBar } from '@/app/modules/shared/components/progress-bar'
import { useNamespacedIntl } from '../lib/useNamespacedIntl'

export type UserCompanyStepValues = {
  companyName: string
  companySize: CompanySize | ''
  companyDescription: string
}

type UserCompanyStepProps = {
  onContinue: () => void | Promise<void>
  maxSteps: number
  currentStep: number
}

const COMPANY_SIZE_VALUES = [
  CompanySize.Small,
  CompanySize.Growing,
  CompanySize.Established,
] as const

export function UserCompanyStep({
  onContinue,
  maxSteps,
  currentStep,
}: UserCompanyStepProps) {
  const t = useNamespacedIntl('UserOnboarding')
  const { setMessage } = useNotificationContext()
  const { data: { data: userProfile } = {} } = useUserProfile()
  const { mutateAsync: updateProfile, isLoading: isMutating } =
    useUserProfileMutation(userProfile?._id as string)

  const companySizeOptions = useMemo(
    () =>
      [
        { value: CompanySize.Small, label: t('company_size_small_team') },
        { value: CompanySize.Growing, label: t('company_size_growing_team') },
        {
          value: CompanySize.Established,
          label: t('company_size_established_company'),
        },
      ] as const,
    [t]
  )

  const formSchema = yup.object().shape({
    companyName: yup
      .string()
      .trim()
      .required(t('validation_company_name_required')),
    companySize: yup
      .string()
      .oneOf([...COMPANY_SIZE_VALUES], t('validation_company_size_required'))
      .required(t('validation_company_size_required')),
    companyDescription: yup
      .string()
      .trim()
      .required(t('validation_company_description_required'))
      .max(5000),
  })

  const methods = useForm<UserCompanyStepValues>({
    resolver: yupResolver(formSchema) as Resolver<UserCompanyStepValues>,
    disabled: isMutating,
    values: {
      companyName: userProfile?.companyName?.trim() ?? '',
      companySize: (userProfile?.companySize as CompanySize) ?? '',
      companyDescription: userProfile?.companyDescription?.trim() ?? '',
    },
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods

  const formRef = useRef<HTMLFormElement>(null)

  const handleOnSubmit = async (data: UserCompanyStepValues) => {
    try {
      await updateProfile({
        companyName: data.companyName.trim(),
        companySize: data.companySize as CompanySize,
        companyDescription: data.companyDescription.trim(),
      })
      await onContinue()
    } catch {
      setMessage(t('profile_update_failed'), INotificationStatus.ERROR)
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        className="flex px-6 pb-3 md:px-0 md:py-8"
        onSubmit={handleSubmit(handleOnSubmit)}
        ref={formRef}
      >
        <div className="flex w-full flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Headline2>{t('company_about_heading')}</Headline2>
            <Typography variant="body2" className="text-primary-light">
              {t('company_about_subtitle')}
            </Typography>
          </div>

          {errors.root?.serverError?.message && (
            <ErrorMessage
              message={errors.root?.serverError?.message}
              className="mt-2 text-left"
            />
          )}

          <div className="flex flex-col gap-2">
            <label
              htmlFor="companyName"
              className="text-sm font-semibold text-secondary"
            >
              {t('company_about_name_label')}
            </label>
            <Controller
              control={control}
              name="companyName"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id="companyName"
                  autoComplete="organization"
                  placeholder={t('company_about_name_placeholder')}
                  errorMessage={getErrorMessage('companyName', errors)}
                  className="w-full text-sm"
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="companySize"
              className="text-sm font-semibold text-secondary"
            >
              {t('company_about_size_label')}
            </label>
            <Controller
              control={control}
              name="companySize"
              render={({ field }) => (
                <div className="relative">
                  <select
                    {...field}
                    id="companySize"
                    disabled={isMutating}
                    className="h-12 w-full appearance-none rounded-xl border border-lines bg-white pl-4 pr-10 text-sm text-mydra-black outline-none focus:border-mydra-purple disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option value="">
                      {t('company_about_size_placeholder')}
                    </option>
                    {companySizeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            />
            {errors.companySize?.message && (
              <ErrorMessage
                message={errors.companySize.message}
                className="text-left"
              />
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="companyDescription"
              className="text-sm font-semibold text-secondary"
            >
              {t('company_about_description_label')}
            </label>
            <Controller
              control={control}
              name="companyDescription"
              render={({ field }) => (
                <textarea
                  {...field}
                  id="companyDescription"
                  rows={6}
                  disabled={isMutating}
                  placeholder={t('company_about_description_placeholder')}
                  className="w-full resize-y rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-mydra-black outline-none placeholder:text-secondary focus:outline focus:outline-1 focus:outline-mydra-black disabled:cursor-not-allowed disabled:bg-mydra-gray"
                />
              )}
            />
            {errors.companyDescription?.message && (
              <ErrorMessage
                message={errors.companyDescription.message}
                className="text-left"
              />
            )}
          </div>

          <div className="mt-auto flex w-full md:justify-end">
            <ProgressBar
              max={maxSteps}
              progress={currentStep}
              className="mr-auto self-center md:w-32"
            />
            <PrimaryButton
              type="submit"
              disabled={isMutating}
              isLoading={isSubmitting}
              className="w-full md:w-auto"
            >
              {t('continue')}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
